import { useEffect, useState } from "react";
import {
  getNextListening,
  listeningAudioUrl,
  submitListening,
  type ListeningExercise,
  type ListeningScore,
} from "../lib/api";

interface Props { onClose: () => void; }

export function ListeningView({ onClose }: Props): JSX.Element {
  const [ex, setEx] = useState<ListeningExercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<string[]>([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ListeningScore | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getNextListening()
      .then((e) => {
        setEx(e);
        setAnswers(e ? new Array(e.questions.length).fill("") : []);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-8 text-center text-sm text-ink-400">Loading…</div>;
  }
  if (error || !ex) {
    return (
      <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-6 text-center">
        <div className="text-white">{error ?? "No listening exercises available at your level."}</div>
        <button onClick={onClose} className="mt-3 text-sm text-ink-400 hover:text-white">← Back</button>
      </div>
    );
  }

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const r = await submitListening(ex!.slug, answers);
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div className="rounded-2xl border border-emerald-700/40 bg-emerald-500/5 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Result</h2>
          <div className="text-3xl font-bold text-emerald-300">{result.overall}%</div>
        </div>
        {result.feedback && <p className="text-sm text-ink-200">{result.feedback}</p>}
        <div className="space-y-2">
          {ex.questions.map((q, i) => {
            const r = result.per_question[i];
            if (!r) return null;
            const color = r.correct ? "border-emerald-700/40" : r.score >= 50 ? "border-amber-700/40" : "border-rose-700/40";
            return (
              <div key={i} className={`rounded-lg border ${color} bg-ink-900/60 p-3 text-sm`}>
                <div className="text-ink-100 font-medium">{i + 1}. {q.q}</div>
                <div className="text-ink-400 mt-1">Your answer: {answers[i] || "—"}</div>
                <div className="text-xs text-ink-300 mt-1">{r.comment}</div>
                <div className="text-xs font-mono text-ink-500 mt-0.5">score {r.score}</div>
              </div>
            );
          })}
        </div>
        <button onClick={onClose} className="w-full rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold py-2.5">
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5">
        <div className="text-[10px] uppercase tracking-widest text-ink-500">{ex.level} · {ex.track} · ~{ex.durationSec}s</div>
        <h2 className="text-lg font-semibold text-white mt-1">{ex.title}</h2>
        {ex.description && <p className="text-sm text-ink-400 mt-1">{ex.description}</p>}

        <audio controls preload="auto" className="w-full mt-3" src={listeningAudioUrl(ex.slug)} />

        <button
          onClick={() => setShowTranscript((v) => !v)}
          className="mt-2 text-xs text-ink-400 hover:text-white underline-offset-2 hover:underline"
        >
          {showTranscript ? "Hide transcript" : "Show transcript"}
        </button>
        {showTranscript && (
          <div className="mt-2 rounded-lg border border-ink-700 bg-ink-800/60 p-3 text-sm text-ink-200 leading-relaxed">
            {ex.transcript}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {ex.questions.map((q, i) => (
          <div key={i} className="rounded-2xl border border-ink-700 bg-ink-900/60 p-4">
            <div className="text-white font-medium">{i + 1}. {q.q}</div>
            {q.hint && <div className="text-xs text-ink-400 mt-1 italic">{q.hint}</div>}
            <textarea
              value={answers[i] ?? ""}
              onChange={(e) => {
                const next = [...answers];
                next[i] = e.target.value;
                setAnswers(next);
              }}
              rows={2}
              placeholder="Your answer…"
              className="mt-2 w-full rounded-xl bg-ink-800 border border-ink-700 px-3 py-2 text-sm text-white placeholder-ink-500 focus:outline-none focus:border-lime-500"
            />
          </div>
        ))}
      </div>

      {error && <div className="text-rose-300 text-sm">{error}</div>}

      <div className="flex items-center justify-between">
        <button onClick={onClose} className="text-xs text-ink-400 hover:text-white">← Cancel</button>
        <button
          onClick={handleSubmit}
          disabled={submitting || answers.every((a) => !a.trim())}
          className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-2.5 disabled:opacity-40"
        >
          {submitting ? "Scoring…" : "Submit answers"}
        </button>
      </div>
    </div>
  );
}
